/**
 * The shopper's own Twitch link, as the account's Twitch connection and the PiP player's Sign in to
 * Twitch both read and change it (boxed's `useTwitchConnection`): GET /v1/me/twitch for where it
 * stands, the store's link popup (`useTwitchLink`) to connect or reconnect, and DELETE
 * /v1/me/twitch to disconnect. Off (no reads at all) unless the store has live breaks and its own
 * Twitch app and the shopper is signed in.
 */
import {
  liveBreakPlatformWords,
  TWITCH_LINK_RESULT_PARAM,
  type TwitchIdentity,
} from "@loot-os/api-contract";
import { LootApiError } from "@loot-os/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

import { lootClient, twitchMeQueryOptions } from "./queries.js";
import { useShopper } from "./shopper.js";
import { useTenant } from "./tenant.js";
import { useTerminology } from "./terminology.js";
import { showToast } from "./toasts.js";
import {
  readTwitchLinkResult,
  refreshAfterTwitchLink,
  type TwitchLinkResult,
  twitchOf,
  useTwitchLink,
} from "./twitch.js";

/** Where the shopper's link stands. */
export type TwitchConnectionStatus =
  /** Being read. */
  | "loading"
  /** No Twitch account linked. */
  | "notLinked"
  | "linked"
  /** Linked, but Twitch stopped accepting it: link again. */
  | "reconnect"
  /** The store can't link Twitch now (the read failed, or the store's app is gone). */
  | "unavailable";

export interface TwitchConnection {
  /** The store links Twitch here at all: live breaks, its Twitch app, a signed-in shopper. */
  available: boolean;
  status: TwitchConnectionStatus;
  /** The linked account (its login, name and whether the link still works); null with none. */
  identity: TwitchIdentity | null;
  /** Opens the link popup; `connecting` while it is open. */
  connect: () => void;
  connecting: boolean;
  disconnect: () => void;
  disconnecting: boolean;
}

/**
 * The shopper's Twitch link and the ways to change it. `onLinked` hears a link that went through
 * (the PiP player remounts, to read its new sign-in). A failed link or unlink goes to `onError`
 * in the platform's words where the page shows it in place (the account's Twitch connection),
 * else to a toast.
 */
export function useTwitchConnection({
  onLinked,
  onError,
}: {
  onLinked?: () => void;
  onError?: (message: string | null) => void;
} = {}): TwitchConnection {
  const { config, hasFeature } = useTenant();
  const { user, sessionToken } = useShopper();
  const terms = useTerminology();
  const available = hasFeature("liveBreaks") && twitchOf(config).enabled && !!user;
  const me = useQuery({ ...twitchMeQueryOptions(), enabled: available && !!sessionToken });

  const handlers = useRef({ onLinked, onError });
  handlers.current = { onLinked, onError };
  const fail = useCallback((message: string) => {
    const { onError: report } = handlers.current;
    if (report) report(message);
    else showToast({ kind: "error", title: message, id: "twitch-link" });
  }, []);
  const onResult = useCallback(
    (result: TwitchLinkResult) => {
      if (result.ok === null) return;
      handlers.current.onError?.(null);
      if (result.ok) {
        handlers.current.onLinked?.();
        return;
      }
      const message = liveBreakPlatformWords(terms).twitchLinkFailed(result.error);
      if (message) fail(message);
    },
    [fail, terms],
  );
  const link = useTwitchLink({ returnTo: currentPath, onResult });
  const unlink = useMutation({
    mutationFn: () => lootClient.twitch.unlink(),
    onMutate: () => handlers.current.onError?.(null),
    onSuccess: () => refreshAfterTwitchLink(),
    onError: (err) => {
      // Already unlinked elsewhere: read the link again rather than say it failed.
      if (isNotLinked(err)) {
        refreshAfterTwitchLink();
        return;
      }
      fail(liveBreakPlatformWords(terms).twitchUnlinkFailed);
    },
  });

  const identity = me.data?.enabled ? me.data.identity : null;
  const status: TwitchConnectionStatus =
    !sessionToken || me.isPending
      ? "loading"
      : me.isError || me.data?.enabled === false
        ? "unavailable"
        : identity === null
          ? "notLinked"
          : identity.status === "linked"
            ? "linked"
            : "reconnect";

  return {
    available,
    status,
    identity,
    connect: link.connect,
    connecting: link.pending,
    disconnect: () => unlink.mutate(),
    disconnecting: unlink.isPending,
  };
}

/**
 * The page to come back to when the link's popup is blocked and the whole tab goes instead, less
 * the result an earlier link left in its query.
 */
export function twitchReturnPath(location: Pick<Location, "pathname" | "search">): string {
  const search = new URLSearchParams(location.search);
  search.delete(TWITCH_LINK_RESULT_PARAM);
  const query = search.toString();
  return query ? `${location.pathname}?${query}` : location.pathname;
}

function currentPath(): string {
  return twitchReturnPath(window.location);
}

function isNotLinked(err: unknown): boolean {
  return err instanceof LootApiError && err.code === "twitch_not_linked";
}

/**
 * A Twitch link the browser wouldn't open as a popup sends the whole tab through Twitch and back
 * to the page it left, with the result in `?twitchLink=` (boxed's `useTwitchRedirectLinkError`).
 * The pages that link Twitch themselves (a live break's, the Account page) read it there; on any
 * other page (the PiP player's Sign in to Twitch) the dock reads it once: a link that went through
 * reads the shopper's link again, a failed one says why in a toast. Either way the result leaves
 * the address, so a reload doesn't say it twice.
 */
export function useTwitchLinkReturn({ handledByPage }: { handledByPage: boolean }): void {
  const router = useRouter();
  const terms = useTerminology();
  const search = useLocation({ select: (location) => location.searchStr });
  useEffect(() => {
    if (handledByPage) return;
    const result = readTwitchLinkResult(search);
    if (!result) return;
    if (result.ok) refreshAfterTwitchLink();
    else if (result.ok === false) {
      const message = liveBreakPlatformWords(terms).twitchLinkFailed(result.error);
      // After this commit's effects: on a page's first load the store's toast outlet, an
      // ancestor, subscribes in its own effect, which runs after this one.
      if (message)
        setTimeout(() => showToast({ kind: "error", title: message, id: "twitch-link" }));
    }
    router.history.replace(twitchReturnPath(window.location));
  }, [handledByPage, router, search, terms]);
}
